import React from 'react'
import { isNull } from 'lodash'
import {
  SortDirectionEnum,
  TestsFull_tests_pagination,
  TestsResultsAnalytics_testsResultsAnalytics_tests,
  getFormattedDateAndTime,
  getResultLabelColor,
  SortByType,
  TestType,
  TestSessionPaymentStatus,
} from '@modmd/data'
import { DEVICE } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import Datalist, { DataListProps } from 'components/Datalist'
import Pagination from 'components/Pagination'
import Label from 'components/Label'
import Card from 'components/Card'
import { TestSessionPaymentLabel } from 'sharedComponents/TestSessionPaymentLabel'
import { Box } from 'components/Layout'
import { isInfluenza } from 'utils/helpers'

interface ColumnLabels {
  patient?: string | null
  barcode?: string | null
  testCategory?: string | null
  result?: string | null
  appointmentDate?: string | null
  testedAt?: string | null
  resultProvisionedAt?: string | null
  labName?: string | null
  paymentStatus?: string | null
  paymentMethod?: string | null
}

interface Props {
  tableHeader?: React.ReactElement
  tableFooter?: React.ReactElement
  tests: TestsResultsAnalytics_testsResultsAnalytics_tests[]
  pagination?: TestsFull_tests_pagination
  sorter?: DataListProps['sorter']
  requestDebounceInMs?: number
  isLoading: boolean
  pageLength?: number
  columnLabels?: ColumnLabels
  onSortChange?: (sortBy: string, direction: SortDirectionEnum) => void
  onPageChange?: (page: number) => void
  onRowClick?: (data: TestsResultsAnalytics_testsResultsAnalytics_tests) => void
}

const defaultColumnLabels = {
  patient: 'Patient',
  insuranceCompany: 'Insurance Company',
  barcode: 'Barcode',
  testCategory: 'Test category',
  result: 'Result',
  appointmentDate: 'Date of appointment',
  testedAt: 'Test date',
  resultProvisionedAt: 'Result date',
  labName: 'Lab name',
  paymentStatus: 'Payment status',
  paymentMethod: 'Payment method',
  zip: 'Zip Code',
}

const PAGE_LENGTH = 10

export const AnalyticsTestsResultsDatalist: React.VFC<Props> = ({
  tableHeader,
  tableFooter,
  tests,
  sorter,
  pagination,
  isLoading,
  columnLabels: providedColumnLabels,
  onSortChange,
  onPageChange,
  onRowClick,
  pageLength = PAGE_LENGTH,
}) => {
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const columnLabels = { ...defaultColumnLabels, ...(providedColumnLabels || {}) }
  const numOfColumns = Object.values(columnLabels).filter((label) => label !== null).length
  const rowGridTemplateColumns = React.useMemo(() => `repeat(${numOfColumns}, 1fr)`, [numOfColumns])

  return (
    <>
      <Card>
        <Datalist
          header={tableHeader}
          isLoading={isLoading}
          skeletonProps={{
            columns: numOfColumns,
            rows: pageLength,
          }}
          data={tests}
          sorter={sorter}
          onSortChange={onSortChange}
          shouldRenderTableHeader={isDesktop}
          showActionIndicators
          renderTableHeader={() => (
            <Datalist.Row gridTemplateColumns={rowGridTemplateColumns}>
              {!isNull(columnLabels.patient) ? (
                <Datalist.HeaderCell label={columnLabels.patient} />
              ) : null}
              {!isNull(columnLabels.insuranceCompany) ? (
                <Datalist.HeaderCell label={columnLabels.insuranceCompany} />
              ) : null}
              {!isNull(columnLabels.barcode) ? (
                <Datalist.HeaderCell label={columnLabels.barcode} />
              ) : null}
              {!isNull(columnLabels.testCategory) ? (
                <Datalist.HeaderCell label={columnLabels.testCategory} />
              ) : null}
              {!isNull(columnLabels.result) ? (
                <Datalist.HeaderCell sortKey={SortByType.RESULT} label={columnLabels.result} />
              ) : null}
              {!isNull(columnLabels.appointmentDate) ? (
                <Datalist.HeaderCell label={columnLabels.appointmentDate} />
              ) : null}
              {!isNull(columnLabels.testedAt) ? (
                <Datalist.HeaderCell label={columnLabels.testedAt} />
              ) : null}
              {!isNull(columnLabels.resultProvisionedAt) ? (
                <Datalist.HeaderCell label={columnLabels.resultProvisionedAt} />
              ) : null}
              {!isNull(columnLabels.labName) ? (
                <Datalist.HeaderCell label={columnLabels.labName} />
              ) : null}
              {!isNull(columnLabels.zip) ? <Datalist.HeaderCell label={columnLabels.zip} /> : null}
              {!isNull(columnLabels.paymentStatus) ? (
                <Datalist.HeaderCell label={columnLabels.paymentStatus} />
              ) : null}
              {!isNull(columnLabels.paymentMethod) ? (
                <Datalist.HeaderCell label={columnLabels.paymentMethod} />
              ) : null}
            </Datalist.Row>
          )}
          renderTableRow={(data) => {
            const {
              barcode,
              Insurance,
              result,
              secondResult,
              resultProvisionedAt,
              TestSession,
              User,
              testedAt,
              eventId,
              eventpaymentMethod,
            } = data
            return (
              <Datalist.Row
                gridTemplateColumns={{
                  _: 'repeat(2, 1fr)',
                  DESKTOP: rowGridTemplateColumns,
                }}
                onClick={
                  onRowClick
                    ? () => {
                        onRowClick(data)
                      }
                    : undefined
                }
                {...(!isDesktop ? { alignItems: 'start' } : {})}
              >
                {!isNull(columnLabels.patient) ? (
                  <Datalist.Cell label={columnLabels.patient}>
                    {User && (
                      <>
                        {User.firstName} {User.lastName} ({getFormattedDateAndTime(User.birthDate)})
                      </>
                    )}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.insuranceCompany) ? (
                  <Datalist.Cell label={columnLabels.insuranceCompany}>
                    {Insurance &&
                    Insurance.insuranceCompany &&
                    TestSession.paymentStatus == TestSessionPaymentStatus.Insurance
                      ? Insurance.insuranceCompany
                      : ''}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.barcode) ? (
                  <Datalist.Cell label={columnLabels.barcode}>
                    <Box>{barcode}</Box>
                    <Box>{eventId && <Label indicator="grayDark">Event: {eventId}</Label>}</Box>
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.testCategory) ? (
                  <Datalist.Cell label={columnLabels.testCategory}>
                    {TestSession.type}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.result) ? (
                  <Datalist.Cell label={columnLabels.result}>
                    <Label indicator={result ? getResultLabelColor(result) : 'blue'}>
                      {isInfluenza(TestSession.type as TestType) && `A: `}
                      {result || 'Pending result'}
                    </Label>
                    {isInfluenza(TestSession.type as TestType) && secondResult && (
                      <Label indicator={getResultLabelColor(secondResult)}>
                        {`B: ${secondResult}`}
                      </Label>
                    )}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.appointmentDate) ? (
                  <Datalist.Cell label={columnLabels.appointmentDate}>
                    {getFormattedDateAndTime(TestSession.date, true)}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.testedAt) ? (
                  <Datalist.Cell label={columnLabels.testedAt}>
                    {getFormattedDateAndTime(testedAt, true)}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.resultProvisionedAt) ? (
                  <Datalist.Cell label={columnLabels.resultProvisionedAt}>
                    {resultProvisionedAt ? (
                      getFormattedDateAndTime(resultProvisionedAt, true)
                    ) : (
                      <Label indicator="red">Not provisionet yet</Label>
                    )}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.labName) ? (
                  <Datalist.Cell label={columnLabels.labName}>{TestSession.lab}</Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.zip) ? (
                  <Datalist.Cell label={columnLabels.zip}>{TestSession.zip}</Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.paymentStatus) ? (
                  <Datalist.Cell label={columnLabels.paymentStatus}>
                    <TestSessionPaymentLabel paymentStatus={TestSession.paymentStatus} />
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.paymentMethod) ? (
                  <Datalist.Cell label={columnLabels.paymentMethod}>
                    {eventpaymentMethod || null}
                    {!eventpaymentMethod &&
                      (TestSession.Company?.id
                        ? TestSession.paymentStatus === TestSessionPaymentStatus.Insurance
                          ? 'Insurance'
                          : 'Invoice'
                        : 'Credit card')}
                  </Datalist.Cell>
                ) : null}
              </Datalist.Row>
            )
          }}
          footer={tableFooter}
        />
      </Card>
      {pagination && pagination.total > pagination.length && onPageChange && (
        <Pagination.Wrapper>
          <Pagination
            firstPage={1}
            lastPage={Math.ceil(pagination.total / pagination.length) || 1}
            currentPage={Math.ceil(pagination.from / pagination.length) + 1}
            onPageChange={onPageChange}
          />
        </Pagination.Wrapper>
      )}
    </>
  )
}
